// Renders a post's parsed Markdown (parse.ts) in the article's typography. Site paths become
// router links; other links open in a new tab. A figure's caption shows its credit apart.
import { Info, Lightbulb, Warning } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, type ReactNode, useMemo } from "react";
import { Link } from "react-router";

import { type Block, type CalloutTone, type Inline, type ListItem, parseMarkdown } from "./parse";

function Inlines({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((p, i) => {
        switch (p.kind) {
          case "text":
            return <Fragment key={i}>{p.text}</Fragment>;
          case "code":
            return (
              <code key={i} className="rounded bg-paper2 px-1.5 py-0.5 font-mono text-[0.84em]">
                {p.text}
              </code>
            );
          case "strong":
            return (
              <strong key={i} className="font-semibold text-ink">
                <Inlines parts={p.children} />
              </strong>
            );
          case "em":
            return (
              <em key={i}>
                <Inlines parts={p.children} />
              </em>
            );
          case "link":
            return p.href.startsWith("/") || p.href.startsWith("#") ? (
              <Link key={i} to={p.href} className="link">
                {p.text}
              </Link>
            ) : (
              <a key={i} href={p.href} className="link" target="_blank" rel="noopener noreferrer">
                {p.text}
              </a>
            );
        }
      })}
    </>
  );
}

function List({ block }: { block: Extract<Block, { kind: "list" }> }) {
  const Tag = block.ordered ? "ol" : "ul";
  return (
    <Tag className={clsx("my-6 space-y-2.5 ps-6 marker:text-signal", block.ordered ? "list-decimal marker:font-mono marker:text-[0.85em]" : "list-disc")}>
      {block.items.map((item: ListItem, i) => (
        <li key={i} className="ps-1.5">
          <Inlines parts={item.text} />
          {item.children ? <List block={item.children} /> : null}
        </li>
      ))}
    </Tag>
  );
}

const CALLOUT: Record<CalloutTone, { Icon: typeof Info; label: string; cls: string }> = {
  note: { Icon: Info, label: "Note", cls: "border-teal/40 bg-teal/[0.06]" },
  tip: { Icon: Lightbulb, label: "Try this", cls: "border-moss/40 bg-moss/[0.06]" },
  warning: { Icon: Warning, label: "Careful", cls: "border-signal/40 bg-signal/[0.05]" },
};

function Caption({ text }: { text: string }) {
  const at = text.lastIndexOf("Credit:");
  const [body, credit] = at >= 0 ? [text.slice(0, at).trim(), text.slice(at).trim()] : [text, ""];
  return (
    <figcaption className="mt-3 font-sans text-[13.5px] leading-snug text-ink2">
      {body} {credit ? <span className="mt-1 block font-mono text-[10.5px] uppercase tracking-wide text-ink3">{credit}</span> : null}
    </figcaption>
  );
}

function BlockView({ block }: { block: Block }): ReactNode {
  switch (block.kind) {
    case "heading": {
      const Tag = `h${block.level}` as "h2" | "h3" | "h4";
      const size = block.level === 2 ? "mt-14 text-[1.9rem] sm:text-[2.2rem]" : "mt-10 text-[1.4rem]";
      return (
        <Tag id={block.id} className={clsx("scroll-mt-24 font-display font-bold leading-tight tracking-[-0.025em] text-ink", size)}>
          <Inlines parts={block.text} />
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p className="my-5">
          <Inlines parts={block.text} />
        </p>
      );
    case "list":
      return <List block={block} />;
    case "quote":
      return (
        <figure className="my-10 border-s-[3px] border-signal ps-6">
          <blockquote className="font-display text-[1.45rem] font-semibold leading-snug tracking-[-0.015em] text-ink sm:text-[1.65rem]">
            {block.paragraphs.map((p, i) => (
              <p key={i} className={i > 0 ? "mt-3" : undefined}>
                <Inlines parts={p} />
              </p>
            ))}
          </blockquote>
          {block.cite ? (
            <figcaption className="mt-3 font-mono text-[12px] uppercase tracking-wider text-ink3">
              — <Inlines parts={block.cite} />
            </figcaption>
          ) : null}
        </figure>
      );
    case "callout": {
      const c = CALLOUT[block.tone];
      return (
        <aside className={clsx("my-8 rounded-2xl border p-5 font-sans text-[15.5px] leading-relaxed text-ink2", c.cls)}>
          <p className="mb-2 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink">
            <c.Icon weight="bold" className="h-4 w-4" aria-hidden /> {c.label}
          </p>
          {block.paragraphs.map((p, i) => (
            <p key={i} className={i > 0 ? "mt-3" : undefined}>
              <Inlines parts={p} />
            </p>
          ))}
        </aside>
      );
    }
    case "figure":
      return (
        <figure className="my-10 sm:-mx-10 lg:-mx-20">
          <img src={block.src} alt={block.alt} loading="lazy" className="w-full rounded-2xl border border-line bg-paper2" />
          <div className="sm:mx-10 lg:mx-20">{block.caption ? <Caption text={block.caption} /> : null}</div>
        </figure>
      );
    case "code":
      return (
        <pre className="my-8 overflow-x-auto rounded-2xl bg-ink p-5 font-mono text-[13px] leading-relaxed text-paper sm:-mx-6" data-lang={block.lang}>
          <code>{block.text}</code>
        </pre>
      );
    case "table":
      return (
        <div className="my-8 overflow-x-auto rounded-2xl border border-line font-sans sm:-mx-6">
          <table className="w-full border-collapse text-left text-[14px]">
            <thead className="bg-paper2">
              <tr>
                {block.head.map((h, i) => (
                  <th key={i} className="px-4 py-3 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink2">
                    <Inlines parts={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-t border-line align-top">
                  {row.map((c, i) => (
                    <td key={i} className="px-4 py-3 text-ink2">
                      <Inlines parts={c} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "rule":
      return (
        <div className="my-12 text-center font-mono text-signal" aria-hidden>
          ✕ ✕ ✕
        </div>
      );
  }
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  return (
    <div className={clsx("font-serif text-[1.16rem] leading-[1.75] text-ink2 sm:text-[1.22rem]", className)}>
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} />
      ))}
    </div>
  );
}
